import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { urlValidation } from 'utils/patterns';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { ControlledInput } from 'components/Inputs/ControlledInput/ControlledInput';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import SelectComponent from 'components/Inputs/SelectComponent';
import { syncTypeOptions } from 'components/Tables/ParsersTable/constants';
import { ParsersTableTransFields } from 'components/Tables/ParsersTable/types';
import { parsersTableTranslations } from 'components/Tables/ParsersTable/translations';
import CronDataSelect from 'components/Inputs/CronDataSelect/CronDataSelect';
import EntityOption from 'components/Core/EntityOption';
import { IBrandRef, IDataOption, KeyOfTypeFromArray } from 'types/common';
import { IParserData, SyncType } from 'stores/parsers/types';
import { IParserForm, ParserFormTransFields } from './types';
import { parserFormTranslations } from './translations';
import styles from './parsersForm.module.scss';

const ParsersForm: FC<{ parserData?: IParserData }> = ({ parserData }) => {
  const {
    parsersStore: { addParser, editParser },
    brandsStore: { getAutoCompleteParsersBrands },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<
    ParserFormTransFields | Extract<ParsersTableTransFields, KeyOfTypeFromArray<SyncType>>
  >([parserFormTranslations.name, parsersTableTranslations.name]);

  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(parserData ? editParser : addParser, {
    mutationKey: 'editParser-addParser',
    onSuccess: (res: IParserData | null) => {
      showAlert(`${res ? 'create' : 'edit'} parser success`, 'success');
      if (res) navigate(`/parsers/${res.id}`);
      else navigate('/parsers');
    },
  });

  const useFormMethods = useForm<IParserForm>({
    defaultValues: {
      ...parserData,
      type: parserData
        ? ({
            id: parserData?.type,
            name: t(parserData?.type, { ns: parsersTableTranslations.name }),
          } as IDataOption<SyncType>)
        : null,
      brand: parserData?.brand || null,
      cronExpression: parserData?.cronExpression || '* * * * *',
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, isSubmitting }, // should create errors conts to update errors when they appears
  } = useFormMethods;

  const onAutocompleteSearch = useCallback((search: string): Promise<IBrandRef[]> => {
    const type = getValues('type');
    if (type) {
      return getAutoCompleteParsersBrands({ search, type: type.id as SyncType });
    } else {
      return Promise.resolve([]);
    }
  }, []);

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  const onSubmitHandler = useCallback((inputData: IParserForm) => {
    mutate(inputData);
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <SelectComponent
                name='type'
                label={t('type')}
                options={syncTypeOptions.map((item) => ({
                  id: item,
                  name: t(item, { ns: parsersTableTranslations.name }),
                }))}
                optionLabel='name'
                optionValue='id'
                onSelect={(value) => {
                  setValue('type', value as IDataOption<SyncType> | null);
                  setValue('brand', null);
                }}
                required
              />
              <AutocompleteSelect
                name='brand'
                label={t(`brand`)}
                data={[]}
                onSearch={onAutocompleteSearch}
                isDisabled={!watch('type')}
                required
                customOption={renderBrandOption}
                customSingleValue={renderBrandOption}
              />
              <ControlledInput
                name='endpoint'
                type='text'
                required
                label={t('endpoint')}
                validation={urlValidation()}
              />
              <ControlledInput name='apiKey' type='text' label={t('apiKey')} required maxLength={100} />
              <CronDataSelect name='cronExpression' label={t('cronExpression')} />
              {parserData && (
                <div className={styles.checkboxWrap}>
                  <h6 className={`${styles.title}`}>{t('status')}</h6>
                  <Switch
                    isActive={watch('status') === 'Active'}
                    onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                  />
                </div>
              )}
            </div>
            <div>
              <Button
                title={t('save')}
                onClick={handleSubmit(onSubmitHandler)}
                isDisabled={isSubmitting || isLoading}
              />
            </div>
          </section>
        </form>
      </div>
    </FormProvider>
  );
};

export default observer(ParsersForm);
